import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedListItemText as ListItemText } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { CheckCircle, Bolt, Lock, Support } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const UpgradePage: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');
  const text = (en: string, hi: string) => isHindi ? hi : en;
  const [selectedPlanType, setSelectedPlanType] = useState<'premium_monthly' | 'premium'>('premium');
  const selectedPlan = selectedPlanType === 'premium'
    ? {
      title: text('Yearly Premium', 'वार्षिक प्रीमियम'),
      price: '₹999',
      period: text('/ Year', '/ वर्ष'),
      description: text('Full-year plan access managed through RevenueCat in supported app builds.', 'Supported app builds में RevenueCat से managed full-year plan access।'),
      duration: text('1 Year', '1 वर्ष'),
    }
    : {
      title: text('Monthly Premium', 'मासिक प्रीमियम'),
      price: '₹199',
      period: text('/ Month', '/ माह'),
      description: text('Flexible monthly plan access managed through RevenueCat in supported app builds.', 'Supported app builds में RevenueCat से managed flexible monthly plan access।'),
      duration: text('1 Month', '1 महीना'),
    };
  const premiumIncludes = [
    { icon: <Bolt />, text: text('Unlimited chart saves', 'अनलिमिटेड चार्ट सेव') },
    { icon: <CheckCircle />, text: text('All advanced analysis tools', 'सभी advanced analysis tools') },
    { icon: <CheckCircle />, text: text('Premium Horoscope Matching', 'प्रीमियम कुंडली मिलान') },
    { icon: <CheckCircle />, text: text('Astro-Vastu with KP + BNN factors, remedies, scores and PDF report', 'KP + BNN आधारित एस्ट्रो-वास्तु, उपाय, स्कोर और PDF रिपोर्ट') },
    { icon: <CheckCircle />, text: text('Watch Recommendation with birth date, goals, colours and current watch analysis', 'जन्म तारीख, लक्ष्य, रंग और current watch analysis के साथ वॉच रिकमेंडेशन') },
    { icon: <CheckCircle />, text: text('Numerology suggestions for favourable numbers, starting letters, house, vehicle, mobile, business and baby names', 'शुभ अंक, अक्षर, मकान, वाहन, मोबाइल, बिजनेस और बच्चे के नाम के सुझाव') },
    { icon: <CheckCircle />, text: text('Dynamic Numerology Dasha Charts and Vedic Grid Yogas', 'डायनामिक न्यूमेरोलॉजी दशा चार्ट्स और वैदिक ग्रिड योग') },
    { icon: <CheckCircle />, text: text('Panch Pakshi timing insights', 'पंच पक्षी समय संकेत') },
    { icon: <CheckCircle />, text: text('Vedic Yogas and personalised gemstone guidance', 'वैदिक योग और व्यक्तिगत रत्न मार्गदर्शन') },
    { icon: <CheckCircle />, text: text('Jaimini analysis with Chara Dasha, Karakamsha and Arudha insights', 'चरा दशा, कारकांश और आरूढ़ insights के साथ जैमिनी विश्लेषण') },
    { icon: <CheckCircle />, text: text('BNN Transit & Progression', 'BNN गोचर और प्रोग्रेशन') },
    { icon: <CheckCircle />, text: text('Personalised Gochar analysis', 'व्यक्तिगत गोचर विश्लेषण') },
    { icon: <CheckCircle />, text: text('Personalised Muhurat with Tarabala and Chandrabala', 'ताराबल और चंद्रबल सहित व्यक्तिगत मुहूर्त') },
    { icon: <CheckCircle />, text: text('Birth Mahurat windows', 'जन्म मुहूर्त विंडो') },
    { icon: <CheckCircle />, text: text('KP HIT Theory Calculator', 'KP HIT थ्योरी कैलकुलेटर') },
    { icon: <CheckCircle />, text: text('KP Fruitful Significator Analyzer', 'KP फलदायी संकेतक विश्लेषक') },
    { icon: <CheckCircle />, text: text('Tajika Varshphal yearly report with monthly timeline', 'मासिक टाइमलाइन सहित ताजिक वर्षफल वार्षिक रिपोर्ट') },
    { icon: <CheckCircle />, text: text('Lal Kitab report with Pakka Ghar, Drishti, Varshphal and age periods', 'पक्का घर, दृष्टि, वर्षफल और age periods सहित लाल किताब रिपोर्ट') },
    { icon: <CheckCircle />, text: text('Chakra insights with Panch Tatva balance and Ishta Devata', 'पंच तत्व बैलेंस और इष्ट देवता सहित चक्र insights') },
    { icon: <CheckCircle />, text: text('Premium Vedic Tarot advanced and Kundli + Tarot spreads', 'Premium Vedic Tarot advanced और Kundli + Tarot spreads') },
    { icon: <CheckCircle />, text: text('PDF report downloads', 'PDF रिपोर्ट डाउनलोड') },
    { icon: <CheckCircle />, text: text('Generate PDFs with your own brand name', 'अपने brand name से PDF बनाएं') },
    { icon: <CheckCircle />, text: text('Unlimited Astro Brand Logo Designer generations', 'अनलिमिटेड Astro Brand Logo Designer generations') },
    { icon: <CheckCircle />, text: text('2 personalised AI reports/month included', 'हर महीने 2 व्यक्तिगत AI रिपोर्ट शामिल') },
    { icon: <CheckCircle />, text: text('Birth time rectification', 'जन्म समय शुद्धि') },
    { icon: <CheckCircle />, text: text('Daily Horoscope and Panchang access', 'दैनिक राशिफल और पंचांग एक्सेस') },
    { icon: <Support />, text: text('Priority email support', 'प्राथमिक ईमेल सहायता') },
    { icon: <CheckCircle />, text: text('1-year subscription', '1 वर्ष की subscription') },
  ];
  const whatYouGet = [
    text('Unlimited chart saves', 'अनलिमिटेड चार्ट सेव'),
    text('All plan features unlocked', 'सभी plan features unlocked'),
    text('Astro-Vastu with remedies and PDF report', 'उपाय और PDF रिपोर्ट के साथ एस्ट्रो-वास्तु'),
    text('Watch Recommendation and current watch analysis', 'वॉच रिकमेंडेशन और current watch analysis'),
    text('Numerology suggestions for favourable numbers, starting letters, house and vehicle numbers', 'शुभ अंक, अक्षर, मकान और वाहन नंबर सुझाव'),
    text('Dynamic Numerology Dasha Charts and Vedic Grid Yogas', 'डायनामिक न्यूमेरोलॉजी दशा चार्ट्स और वैदिक ग्रिड योग'),
    text('Horoscope Matching included', 'कुंडली मिलान शामिल'),
    text('Panch Pakshi timing insights', 'पंच पक्षी समय संकेत'),
    text('Tajika Varshphal, Lal Kitab and Chakra insight modules', 'ताजिक वर्षफल, लाल किताब और चक्र insight modules'),
    text('Jaimini, premium Tarot, BNN Transit, Progression, and KP HIT tools', 'जैमिनी, premium Tarot, BNN गोचर, प्रोग्रेशन और KP HIT tools'),
    text('Personalised Gochar guidance', 'व्यक्तिगत गोचर मार्गदर्शन'),
    text('Personalised Muhurat using Tarabala and Chandrabala', 'ताराबल और चंद्रबल से व्यक्तिगत मुहूर्त'),
    text('Birth Mahurat guidance windows', 'जन्म मुहूर्त guidance windows'),
    text('Priority support', 'प्राथमिक सहायता'),
    text('Instant activation', 'तुरंत activation'),
    text('PDF report downloads', 'PDF रिपोर्ट डाउनलोड'),
    text('Generate PDFs with your own brand name', 'अपने brand name से PDF बनाएं'),
    text('Unlimited Astro Brand Logo Designer generations', 'अनलिमिटेड Astro Brand Logo Designer generations'),
    text('Personalised Kundli reports', 'व्यक्तिगत कुंडली रिपोर्ट'),
    text('Mobile and Name corrections', 'मोबाइल और नाम corrections'),
  ];
  const comparisonRows = [
    { feature: text('Chart Saves', 'चार्ट सेव'), free: '10', premium: '∞' },
    { feature: text('Core Nadi, KP, Vedic', 'मुख्य नाडी, KP, वैदिक'), free: '✓', premium: '✓' },
    { feature: text('Daily Horoscope', 'दैनिक राशिफल'), free: '✓', premium: '✓' },
    { feature: text('Panchang & Ruling Planets', 'पंचांग और रूलिंग प्लैनेट्स'), free: '✓', premium: '✓' },
    { feature: text('Birth Time Rectification', 'जन्म समय शुद्धि'), free: '✓', premium: '✓' },
    { feature: text('Astro-Vastu', 'एस्ट्रो-वास्तु'), free: '✗', premium: '✓' },
    { feature: text('Watch Recommendation', 'वॉच रिकमेंडेशन'), free: '✗', premium: '✓' },
    { feature: text('Numerology Suggestions', 'अंक ज्योतिष सुझाव'), free: '✗', premium: '✓' },
    { feature: text('Dynamic Numerology Dasha Charts', 'डायनामिक न्यूमेरोलॉजी दशा चार्ट्स'), free: '✗', premium: '✓' },
    { feature: text('Vedic Grid Yogas', 'वैदिक ग्रिड योग'), free: '✗', premium: '✓' },
    { feature: text('Panch Pakshi', 'पंच पक्षी'), free: '✗', premium: '✓' },
    { feature: text('Vedic Yogas & Gemstones', 'वैदिक योग और रत्न'), free: '✗', premium: '✓' },
    { feature: text('Jaimini Analysis', 'जैमिनी विश्लेषण'), free: '✗', premium: '✓' },
    { feature: text('BNN Transit Analysis', 'BNN गोचर विश्लेषण'), free: '✗', premium: '✓' },
    { feature: text('Personalised Gochar', 'व्यक्तिगत गोचर'), free: '✗', premium: '✓' },
    { feature: text('Personalised Muhurat', 'व्यक्तिगत मुहूर्त'), free: '✗', premium: '✓' },
    { feature: text('Birth Mahurat', 'जन्म मुहूर्त'), free: '✗', premium: '✓' },
    { feature: text('BNN Progression Engine', 'BNN प्रोग्रेशन इंजन'), free: '✗', premium: '✓' },
    { feature: text('KP HIT Calculator', 'KP HIT कैलकुलेटर'), free: '✗', premium: '✓' },
    { feature: text('Tajika Varshphal', 'ताजिक वर्षफल'), free: '✗', premium: '✓' },
    { feature: text('Lal Kitab', 'लाल किताब'), free: '✗', premium: '✓' },
    { feature: text('Chakra Insights', 'चक्र insights'), free: '✗', premium: '✓' },
    { feature: text('Premium Vedic Tarot', 'Premium Vedic Tarot'), free: '✗', premium: '✓' },
    { feature: text('Horoscope Matching', 'कुंडली मिलान'), free: '✗', premium: '✓' },
    { feature: text('PDF Reports', 'PDF रिपोर्ट'), free: '✗', premium: '✓' },
    { feature: text('Own Brand PDF Reports', 'अपने brand वाली PDF रिपोर्ट'), free: '✗', premium: '✓' },
    { feature: text('Astro Brand Logo Designer', 'Astro Brand Logo Designer'), free: '2 lifetime', premium: '∞' },
    { feature: text('Personalised Reports', 'व्यक्तिगत रिपोर्ट'), free: '✗', premium: '✓' },
    { feature: text('Priority Support', 'प्राथमिक सहायता'), free: '✗', premium: '✓' },
    { feature: text('Mobile and Name corrections', 'मोबाइल और नाम corrections'), free: '✗', premium: '✓' },
  ];
  const faqs = [
    {
      q: text('How long is the premium subscription?', 'प्रीमियम subscription कितने समय की है?'),
      a: text('Premium subscription is valid for 1 year from the date of purchase.', 'प्रीमियम subscription खरीद की तारीख से 1 वर्ष तक valid है।'),
    },
    {
      q: text('Can I cancel my subscription?', 'क्या मैं subscription cancel कर सकता हूं?'),
      a: text('Yes, you can. However, no refunds are provided after 7 days of purchase.', 'हाँ, कर सकते हैं। लेकिन purchase के 7 दिन बाद refund नहीं मिलता।'),
    },
    {
      q: text('Is payment secure?', 'क्या payment secure है?'),
      a: text('Purchases are managed through RevenueCat in supported app builds.', 'Supported app builds में purchases RevenueCat से manage होते हैं।'),
    },
    {
      q: text('Will my charts be saved?', 'क्या मेरे charts save रहेंगे?'),
      a: text('Yes, all charts created with premium are saved permanently and will be accessible until your subscription expires.', 'हाँ, premium में बनाए गए charts save रहेंगे और subscription expiry तक accessible होंगे।'),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Helmet>
        <title>{text('Upgrade Plan | Studio', 'अपग्रेड प्लान | Studio')}</title>
        <meta
          name="description"
          content={text(
            'Upgrade to Studio Pro and unlock unlimited chart access and all premium features.',
            'Studio Pro अपग्रेड करें और अनलिमिटेड चार्ट एक्सेस व सभी premium features पाएं।'
          )}
        />
      </Helmet>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Chip
          label={text('Plan Features', 'प्रीमियम फीचर्स')}
          color="secondary"
          size="small"
          sx={{ mb: 2, fontWeight: 600 }}
        />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
          {text('Choose Plan', 'प्रीमियम अपग्रेड करें')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 760, mx: 'auto' }}>
          {text('Unlock unlimited chart access, advanced features, and premium support. Instant activation with secure payment.', 'अनलिमिटेड चार्ट एक्सेस, advanced features और premium support पाएं। Secure payment के साथ तुरंत activation।')}
        </Typography>
      </Box>

      <Grid container spacing={4} alignItems="stretch">
        {/* Features List */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                {text('Premium Includes', 'प्रीमियम में शामिल')}
              </Typography>

              <List sx={{ '& .MuiListItem-root': { px: 0, py: 1.5 } }}>
                  {premiumIncludes.map((feature, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature.text}
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { fontWeight: 500 }
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 3 }} />

              <Alert severity="success" icon={<CheckCircle />}>
                <strong>{text('Instant Activation:', 'तुरंत Activation:')}</strong> {text('Your subscription activates immediately after payment. No waiting required!', 'Payment के तुरंत बाद subscription activate हो जाती है। कोई waiting नहीं।')}
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Pricing & Payment */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #2e7d52 0%, #1e5a3a 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background decoration */}
            <Box
              sx={{
                position: 'absolute',
                top: -100,
                right: -100,
                width: 300,
                height: 300,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.05)',
              }}
            />

            <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
              <Stack spacing={3}>
                {/* Price Header */}
                <Box>
                  <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                    {selectedPlan.title}
                  </Typography>
                  <Box sx={{ display: 'flex', bgcolor: 'rgba(255,255,255,0.14)', borderRadius: 2, p: 0.5, gap: 0.5, mt: 1.5 }}>
                    {(['monthly', 'yearly'] as const).map((cycle) => {
                      const planType = cycle === 'monthly' ? 'premium_monthly' : 'premium';
                      const active = selectedPlanType === planType;
                      return (
                        <Button
                          key={cycle}
                          fullWidth
                          size="small"
                          onClick={() => setSelectedPlanType(planType)}
                          sx={{
                            bgcolor: active ? '#fff' : 'transparent',
                            color: active ? 'primary.main' : '#fff',
                            fontWeight: 600,
                            '&:hover': { bgcolor: active ? '#fff' : 'rgba(255,255,255,0.12)' },
                          }}
                        >
                          {cycle === 'monthly' ? text('Monthly', 'मासिक') : text('Yearly', 'वार्षिक')}
                        </Button>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1.5 }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'white' }}>
                      {selectedPlan.price}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      {selectedPlan.period}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 1, fontSize: '0.95rem' }}>
                    {selectedPlan.description}
                  </Typography>
                </Box>

                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />

                {/* What's Included */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    {text('What You Get:', 'आपको क्या मिलेगा:')}
                  </Typography>
                  <Stack spacing={1}>
                    {whatYouGet.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 18 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />

                {/* Payment Button */}
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.5,
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                  }}
                  onClick={() => navigate('/profile')}
                >
                  {text('Manage with RevenueCat', 'RevenueCat से manage करें')}
                </Button>

                {/* Security info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', opacity: 0.85 }}>
                  <Lock sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    {text('RevenueCat subscription management', 'RevenueCat subscription management')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Comparison Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
          {text('Plan Comparison', 'प्लान तुलना')}
        </Typography>
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              overflowX: 'auto',
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
              },
              '& th, & td': {
                p: 2,
                textAlign: 'left',
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& th': {
                bgcolor: 'primary.light',
                color: 'primary.main',
                fontWeight: 600,
              },
              '& tr:last-child td': {
                borderBottom: 'none',
              },
            }}
          >
            <table>
              <thead>
                <tr>
                  <th>{text('Feature', 'फीचर')}</th>
                  <th style={{ textAlign: 'center' }}>{text('Free', 'फ्री')}</th>
                  <th style={{ textAlign: 'center' }}>{text('Premium', 'प्रीमियम')}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.feature}>
                    <td>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.feature}
                      </Typography>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Typography variant="body2">{row.free}</Typography>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {row.premium}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Card>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          {text('Frequently Asked Questions', 'अक्सर पूछे जाने वाले सवाल')}
        </Typography>
        <Grid container spacing={2}>
          {faqs.map((faq, idx) => (
            <Grid size={{ xs: 12, sm: 6 }} key={idx}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  {faq.q}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {faq.a}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Support Section */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {text('Questions? Need help?', 'सवाल हैं? सहायता चाहिए?')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            href="https://wa.me/918284927820"
            target="_blank"
            rel="noreferrer"
          >
            {text('WhatsApp Support', 'WhatsApp सहायता')}
          </Button>
          <Button
            variant="outlined"
            href="mailto:info@kundlistarstudio.com"
          >
            {text('Email Us', 'Email करें')}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default UpgradePage;
